/*
Валидаторы используются в инпутах,
должен быть массивом из массивов [регулярка, текст ошибки]
или функцией, возвращающей ошибку
*/

const minLength = (l: number) => (v: string) => v && v.length < l && `Минимум символов: ${l}`;
const maxLength = (l: number) => (v: string) => v.length > l && `Максимум символов: ${l}`;
const checkSymbols = (v: string, regex = /^[а-яё _-]+$/i) => v && !v.match(regex) && 'Некорректные символы. Используйте кириллицу и знак "-"';
const checkSymbolsExtended = (v: string, regex = /^[а-яё0-9a-z_-\s!?.,;:@()]+$/i) => v && !v.match(regex) && 'Некорректные символы';
const required = (v: string, error = 'Обязательное поле'): string | boolean => !v?.length && error || false;
const isFutureDate = (day: number, month: number, year: number): boolean => {
  const inputDate = new Date(year, month - 1, day);
  const today = new Date();
  return inputDate > today;
};

function checkDate(dateString: string): string {
  const error = 'Неверная дата';
  // First check for the pattern
  if (!/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(dateString)) return error;
  // Parse the date parts to integers
  const parts = dateString.split('.');
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);
  // Check the ranges of month and year
  if (year < 1900 || year > new Date().getFullYear() || month === 0 || month > 12) return error;
  if (isFutureDate(day, month, year)) return error;
  const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  // Adjust for leap years
  if (year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) monthLength[1] = 29;
  // Check the range of the day
  if (day <= 0 || day > monthLength[month - 1]) {
    return error;
  }
  return '';
}
const checkUnderage = (inputBirthday: string): boolean | string => {
  const [day, month, year] = inputBirthday.split('.');
  const birthDate = new Date(`${year}-${month}-${day}`);

  const today = new Date();
  const eighteenYearsAgo = new Date(today.getFullYear() - 18, today.getMonth(), today.getDate());

  const isChild = birthDate < eighteenYearsAgo;
  return isChild && 'Ребёнок - до 18 лет';
};
const numbersOnly = (v: string, error = 'Используйте цифры') => v && !v.match(/^[0-9]*$/) && error || false;

const validators: Record<string, TRule[]> = {
  email: [
    (v: string) => (!v.length ? 'Укажите Email' : ''),
    [/^[a-zA-Z0-9_.+-]+@([a-zA-Z0-9-]+\.)+([a-zA-Z0-9-]){2,}$/, 'Некорректный Email'], // не менять сортировку! регуларка используется в других местах
    (v: string): string => (v.length < 5 ? 'Минимальное количество символов: 5' : ''),
    (v: string): string => (v.length > 129 ? 'Максимальное количество символов: 129' : ''),
  ],
  required: [required],
  phone: [
    (v: string) => required(v, 'Укажите телефон'),
    (v: string) => (v.replace(/\D/g, '').length !== 11 ? 'Некорректный номер телефона' : ''),
  ],
  name: [
    // ФИО
    (v: string) => required(v, 'Укажите Имя'),
    checkSymbols,
    minLength(2),
    maxLength(64),
  ],
  surname: [checkSymbols, minLength(2), maxLength(64)],
  lastName: [
    (v: string) => required(v, 'Укажите Фамилию'),
    checkSymbols,
    minLength(2),
    maxLength(64),
  ],
  patronymic: [
    (v: string) => required(v, 'Укажите Отчество'),
    checkSymbols,
    minLength(2),
    maxLength(64),
  ],
  date: [checkDate],
  child_date: [checkDate, checkUnderage],
  code: [
    // Код подтверждения sms/email
    [/^[0-9]*$/, 'Код должен содержать только цифры'],
    [/^[0-9]{6}$/, 'Код должен состоять из 6 символов'],
  ],
  code_call: [
    // Код подтверждения последние 4 цифры номера входящего звонка
    [/^[0-9]*$/, 'Код должен содержать только цифры'],
    [/^[0-9]{4}$/, 'Код должен состоять из 4 символов'],
  ],
  postal_code: [
    // Индекс
    numbersOnly,
    maxLength(6),
  ],
  street: [
    // Улица
    (v: string) => required(v, 'Укажите название улицы'),
    [/^[()0-9а-яёa-zA-Z :;.,/\\-]*$/i, 'Некорректные символы'],
    maxLength(200),
  ],
  house: [
    (v: string) => required(v, 'Укажите номер дома'),
    [/^[0-9а-яё :;.,/\\-]*$/i, 'Используйте кириллицу'],
    maxLength(50),
  ],
  address_name: [
    (v: string) => required(v.trim(), 'Укажите название для адреса'),
    maxLength(128),
  ],
  address_detail: [
    // Дом, Корпус, Квартира, Строение, Подъезд
    [/^[0-9а-яёa-z ]*$/i, 'Некорректные символы'],
    maxLength(10),
  ],
  floor: [
    // Этаж
    numbersOnly,
    maxLength(3),
  ],
  postalCode: [
    numbersOnly,
    (v: string) => {
      if (v.length && v.length !== 6) {
        return 'Должен содержать 6 цифр';
      }
      return false;
    },
  ],
  gender: [(v: string) => required(v, 'Укажите пол')],
  question: [(v: string) => required(v, 'Укажите вопрос'), checkSymbolsExtended, maxLength(1000)],
  barcode: [
    numbersOnly,
    (v: string) => String(v).length !== 13 && 'Проверьте правильность написания. Необходимо ввести 13 цифр',
  ],
};

export default validators;

export type TValidatorKey = keyof typeof validators;

export type TRule = TRuleFn | TRuleRegExp;

type TRuleFn =
  | ((value: string | number) => string | boolean)
  | ((value: string, err?: string) => string | boolean)
  | ((value: string, reg?: RegExp) => string | boolean);

type TRuleRegExp = [RegExp, string];
